import axios from "axios";
import { setSize, defaultSize } from "../../canvas/geometry";
import { generateBlobFromBase64, loadImageFromBlob } from "../../canvas/canvasUtils";
import { imageAlreadyLoaded } from "./helpers";
import { PRELOAD_OPTIONS } from "../../config/config";

export const getThumbnailsForOpenExams = ({ dispatch, getters }) =>
  dispatch("loadImages_thumb", getters.imagesForOpenExams);

export const loadImagesForSideBySide = ({ dispatch, getters }) => {
  let leftImages = getters.imagesByExSrNo(getters.sideBySideLeftExSrNo);
  let rightImages = getters.imagesByExSrNo(getters.sideBySideRightExSrNo);
  let images = [...leftImages, ...rightImages];
  let preLoadOption = getters.preLoadOption;

  if(preLoadOption == PRELOAD_OPTIONS.preLoad2Steps){
    let context = getters.sideBySideContext;
    let mainImages = [];
    mainImages.push(context.leftContext.image);
    mainImages.push(context.rightContext.image);
    return dispatch("loadImages_thumb", images)// load thumb for side panels if not loaded previously
    .then(() => dispatch("preLoad_2Steps", { images, mainImages}));  
  }
  else if(preLoadOption == PRELOAD_OPTIONS.preLoadAll){
    return dispatch("loadImages_thumb", images)// load thumb for side panels if not loaded previously
    .then(() => dispatch("preload_all", images));  
  }
  else{ // PreLoadNone
    return dispatch("loadImages_full_thumb", images);   
  }    
};

export const preload_all = ({ dispatch, commit }, images) => {
  commit("setLoadingMessage", "Loading Image");
  
  return dispatch("loadImages_full", images).finally(() => {
    commit("setLoadingMessage", null);
  });
}

export const preLoad_2Steps = ({ dispatch, commit }, {images, mainImages}) => {
  commit("setLoadingMessage", "Loading Image");

  return dispatch("loadImages_full", mainImages).finally(() => {
    commit("setLoadingMessage", null);
    dispatch("loadImages_full", images);
  });
}

export const loadImages_full_thumb = ({ dispatch }, images) =>
  dispatch("loadImageSet", { images, thumb: true, full: true });

export const loadImages_full = ({ dispatch }, images) =>
  dispatch("loadImageSet", { images });

export const loadImages_thumb = ({ dispatch }, images) =>
  dispatch("loadImageSet", { images, thumb: true, full: false });

export const loadImages_proof = ({ dispatch }, images) =>
  dispatch("loadImageSet", { images, proof: true, full: false });

export const loadImageSet = ({ commit, dispatch }, { images, queueKey = "imageQueue", thumb = false, proof = false, full = true }) => {
  commit("purgeOldImages", images);
  commit("clearImageQueue", queueKey);
  let queue = constructImageQueue(images, thumb, proof, full);
  commit("setImageQueue", { queue, queueKey });
  return dispatch("loadQueue", queueKey);
};

function constructImageQueue(images, thumb, proof, full) {
  let line = { next: 0, queue: [], concurrent: 5, cancelled: false, action: "loadImage" };
  images.forEach(image => {
    addQueueRecord(line.queue, image, "img", full);
    addQueueRecord(line.queue, image, "thumb", thumb);
    addQueueRecord(line.queue, image, "proof", proof);
  });
  return line;
}

function addQueueRecord(queue, image, key, doAdd) {
  if (doAdd && !imageAlreadyLoaded(image, key))
    queue.push({ image, thumb: key == "thumb", proof: key === "proof", prefix: key });
}

export const loadQueue = ({ dispatch, getters }, queueKey) => {
  let line = getters[queueKey];
  if (!line || line.cancelled || !line.queue || !line.queue.length) return Promise.resolve();
  // to prevent crashes from loading too many images at once, load in batches
  let max = Math.min(15, line.concurrent, line.queue.length);
  let promises = [];
  for (let i = 0; i < max; i++) promises.push(dispatch("loadQueueInstance", queueKey));
  return Promise.all(promises);
};

export const loadQueueInstance = ({ dispatch, getters }, queueKey) => {
  let line = getters[queueKey];
  if (!line || line.cancelled || !line.queue || !line.queue.length) return Promise.resolve();
  let item = line.queue[line.next++];
  if (!item) return;
  return dispatch(line.action, item)
    // when the image is loaded, try to grab the next image in the queue
    .finally(() => dispatch("loadQueueInstance", queueKey));
};

export const loadImage = ({ dispatch }, { image, thumb, proof, prefix }) => {
  let request = { ...image, thumb, proof }, action = "getAxisImage", mutation = "setImageState";
  return dispatch("doLoadImage", { request, action, mutation, image, prefix });
};

export const doLoadImage = ({ commit, dispatch }, { image, request, action, mutation, prefix }) => {
  // if image was already loaded, don't load it again
  if (imageAlreadyLoaded(image, prefix)) return Promise.resolve();
  let isLoadingKey = prefix + "IsLoading",
    notFoundKey = prefix + "NotFound";

  let imageState = { [isLoadingKey]: true, [prefix + "IsQueued"]: false, imgKey: image.imgKey };
  commit(mutation, imageState);
  return dispatch(action, request)
    .then(response => loadImageFromString(response.data))
    .then(data => {
      imageState[prefix + "HtmlObject"] = data.img;
      imageState[prefix + "ObjectUrl"] = data.url;
      imageState[prefix + "Size"] = defaultSize();
      setSize(imageState[prefix + "Size"], data.img);
    }).catch(() => {
      imageState[notFoundKey] = true;
    }).finally(() => {
      imageState[isLoadingKey] = false;
      commit(mutation, imageState);

      if (image.imageType === "newApiColorMap")
        commit("loadThicknessMap", image.imgKey);
    });
};

export const getAxisImage = (context, image) =>
  axios.get("/api/image/axisImage", {
    params: {
      thumb: image.thumb,
      proof: image.proof,
      ptSrNo: image.ptSrNo,
      exSrNo: image.exSrNo,
      flSrNo: image.flSrNo,
      flName: image.flName,
      imgType: image.imgType,
      imgSubType: image.imgSubType,
      storeSrNo: image.storeSrNo
    }
  }).catch(error => Promise.reject(error.response));

function loadImageFromString(data) {
  if (!data) return Promise.reject();
  let blob = generateBlobFromBase64(data);
  return loadImageFromBlob(blob)
    .then(({ img, url }) => Promise.resolve({
      img,
      url,
      src: "data:image/jpeg;base64," + data
    }));
}